import { useCallback, useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { keyBy, orderBy as sortBy } from 'lodash'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import DataTable from '../DataTable'
import RefreshButton from '../buttons/RefreshButton'
import LongName from '../DataTable/renders/LongName'
import LongText from '../DataTable/renders/LongText'
import messages from '../../shared/messages'
import { getCustomerUsers } from '../../apis/customerUsers'
import { getProjectMembers } from '../../apis/project'
import { enhanceProjectMembers } from '../../helpers/project'
import { resolveApiAccountOwners } from '../../helpers/apiAccounts'
import { getNameInitials } from '../../helpers/common'
import { getMemberType, getMemberTypeLabelId } from '../../helpers/memberTypes'
import { useTypedSelector } from '../../reducers'

const localMessages = defineMessages({
  title: {
    id: 'screens.folderMembers.title'
  },
  description: {
    id: 'projectAccess.nav.overview.description'
  },
  deletedMember: {
    id: 'screens.folderMembers.deletedMember'
  }
})

export interface IFolderMember {
  userId: string
  name?: string
  email?: string
  principalType?: string
  role?: string
}

const StyledTableTopHeader = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(3),
  backgroundColor: '#f2f5f7'
}))

const StyledTableTopActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  paddingTop: theme.spacing(1)
}))

const StyledNameCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}))

const StyledAvatar = styled(Avatar)({
  width: 28,
  height: 28,
  fontSize: 12
})

const FolderMembersOverview = ({ projectId }: { projectId: string }) => {
  const customerUsers = useTypedSelector(state => state['customerUsers'].data)
  const intl = useIntl()
  const [members, setMembers] = useState<IFolderMember[]>([])
  const [principals, setPrincipals] = useState({})
  const [loading, setLoading] = useState(true)
  const [reloadToken, setReloadToken] = useState(0)
  const [sortOrder, setSortOrder] = useState<string[]>(['asc'])
  const [sortOrderBy, setSortOrderBy] = useState<string[]>(['name'])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    let cancelled = false

    setLoading(true)
    setMembers([])
    setPage(0)

    Promise.all([
      getProjectMembers(projectId).catch(() => [] as IFolderMember[]),
      resolveApiAccountOwners(projectId),
      // The customerUsers store is only loaded when the folder list mounts.
      getCustomerUsers(projectId, '')
    ])
      .then(([memberList, apiAccountOwners, principalList]) => {
        if (cancelled) {
          return
        }
        // Inherited members are left out for now, until there's a clearer way
        // to present them.
        setPrincipals(keyBy(principalList || [], 'id'))
        setMembers(
          ((memberList || []) as IFolderMember[]).map(member =>
            member.principalType === 'OpenAPIAccount' &&
            !member.name &&
            apiAccountOwners[member.userId]
              ? { ...member, name: apiAccountOwners[member.userId].name }
              : member
          )
        )
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [projectId, reloadToken])

  const onRefresh = useCallback(() => setReloadToken(token => token + 1), [])

  const sortedMembers: IFolderMember[] = useMemo(() => {
    if (loading) {
      return []
    }
    const enhanced: IFolderMember[] =
      enhanceProjectMembers(members, { ...customerUsers, ...principals }) || []

    return sortBy(enhanced, sortOrderBy, sortOrder as ('asc' | 'desc')[])
  }, [loading, members, customerUsers, principals, sortOrderBy, sortOrder])

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage
    return sortedMembers.slice(startIndex, startIndex + rowsPerPage)
  }, [sortedMembers, page, rowsPerPage])

  const nameRender = (value: string, item: IFolderMember) => {
    const memberType = getMemberType(item?.principalType)
    const displayName = value || intl.formatMessage(localMessages.deletedMember)

    return (
      <StyledNameCell>
        <StyledAvatar>
          {memberType ? memberType.icon : getNameInitials(displayName)}
        </StyledAvatar>
        <LongName longName={displayName} />
      </StyledNameCell>
    )
  }

  const typeRender = (_value: string, item: IFolderMember) => (
    <LongName
      longName={intl.formatMessage({
        id: getMemberTypeLabelId(item?.principalType)
      })}
    />
  )

  const textRender = (value: string) => <LongText longText={value || ''} />

  const onHandleRequestSort = (field: string, order: string) => {
    setSortOrder([order])
    setSortOrderBy([field])
    setPage(0)
  }

  return (
    <>
      <StyledTableTopHeader>
        <Typography variant={'h4'}>
          {intl.formatMessage(localMessages.title)}
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={1}>
          {intl.formatMessage(localMessages.description)}
        </Typography>
        <StyledTableTopActionButtons>
          <RefreshButton onClick={onRefresh} />
        </StyledTableTopActionButtons>
      </StyledTableTopHeader>
      <DataTable
        loading={loading}
        columns={[
          {
            field: 'name',
            label: intl.formatMessage(messages.name),
            render: nameRender
          },
          {
            field: 'principalType',
            label: intl.formatMessage(messages.type),
            render: typeRender
          },
          {
            field: 'email',
            label: intl.formatMessage(messages.email),
            render: textRender
          },
          {
            field: 'role',
            label: intl.formatMessage(messages.role),
            render: textRender
          }
        ]}
        idField={'userId'}
        data={paginatedData}
        hasSelection={false}
        onHandleRequestSort={onHandleRequestSort}
        headerColor={'#fff'}
        topOffset={0}
        totalCount={sortedMembers.length}
        page={page}
        _rowsPerPage={rowsPerPage}
        onChangePage={setPage}
        onChangeRowsPerPage={(newRowsPerPage: number) => {
          setRowsPerPage(newRowsPerPage)
          setPage(0)
        }}
        _order={sortOrder}
        _orderBy={sortOrderBy}
      />
    </>
  )
}

export default FolderMembersOverview
